import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CSpinner,
} from '@coreui/react-pro'
import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import GameNotificationService from 'src/service/GameNotificationService'
import ToastComponent from 'src/components/common/TaostComponent'
import useSponsorQuillModules from '../Sponsor/useSponsorQuillModules'

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required').max(50, '50 Character Limit is allowed.'),
})

const EditForm = ({ notificationId, visible, onClose, fetchUsers }) => {
  const [notificationDetail, setNotificationDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const quillRef = useRef()
  const modules = useSponsorQuillModules(quillRef)

  useEffect(() => {
    if (visible && notificationId) {
      setDetailLoading(true)
      GameNotificationService.getNotificationDetail(notificationId)
        .then((res) => {
          if (res.status === 200) {
            setNotificationDetail(res.data)
          }
        })
        .catch((e) => {
          ToastComponent(e.response?.data?.message || 'Something went wrong.', 'error')
        })
        .finally(() => setDetailLoading(false))
    }
  }, [visible, notificationId])

  const formik = useFormik({
    initialValues: {
      title: notificationDetail?.title || '',
      message: notificationDetail?.message || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      setLoader(true)
      GameNotificationService.editNotification({ ...data, notificationId })
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers?.()
            ToastComponent(res.message, 'success')
            onClose()
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
        })
        .finally(() => setLoader(false))
    },
  })

  const handleClose = () => {
    setErrorMessages([])
    setNotificationDetail(null)
    formik.resetForm()
    onClose()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 560 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Notification</COffcanvasTitle>
          <div className="text-medium-emphasis small">
            Update {notificationDetail?.title || 'this notification'}&apos;s title and message.
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        {detailLoading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading notification details...</span>
          </div>
        ) : (
          <CForm className="row g-3 flex-grow-1 align-content-start" onSubmit={formik.handleSubmit}>
            <CCol xs={12}>
              <CFormLabel htmlFor="title">Title *</CFormLabel>
              <CFormInput
                id="title"
                name="title"
                placeholder="Title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.errors.title && formik.touched.title)}
              />
              {formik.errors.title && formik.touched.title && (
                <CFormFeedback invalid>{formik.errors.title}</CFormFeedback>
              )}
            </CCol>
            <CCol xs={12}>
              <CFormLabel htmlFor="message">Message</CFormLabel>
              <div className="gn-editor">
                <ReactQuill
                  theme="snow"
                  ref={quillRef}
                  value={formik.values.message}
                  onChange={(content) => formik.setFieldValue('message', content)}
                  modules={modules}
                />
              </div>
            </CCol>
          </CForm>
        )}
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
          Save Changes
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default EditForm
